import React from 'react';
import { fetchServiceSchedule } from '../services/DcsaApiClient';
import ExcelExport from './ExcelExport';
import RefreshButton from './RefreshButton';
import LoadingIndicator from './LoadingIndicator';
import { DatabaseService } from '../services/DatabaseService';
import ScheduleGrid from './ScheduleGrid';
import { SAECS_VESSEL_IMOS } from '../services/serviceConfigs';

const SAECS_SERVICE_CODE = '278';
const VESSELS_PER_GRID = 8;

const VesselHeader = ({ vessel }) => {
  return (
    <div>
      <div className="px-2 py-1 font-medium text-gray-900 dark:text-white">
        {vessel.vesselName || vessel.name}<br/>{vessel.voyageNumber || vessel.voyage}
      </div>
      <div className="grid grid-cols-2 border-t dark:border-gray-600">
        <div className="px-2 py-1 border-r dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300">ETA</div>
        <div className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">ETD</div>
      </div>
    </div>
  );
};

const ServiceScheduleView = () => {
  const [scheduleData, setScheduleData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadScheduleData = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // If not forcing refresh, try to get data from database first
      if (!forceRefresh) {
        console.log('[SAECS] Checking database for cached data...');
        const dbResult = await DatabaseService.getServiceSchedules('SAECS');

        if (dbResult && dbResult.data) {
          // Check if data is recent (less than 30 minutes old)
          const lastUpdated = new Date(dbResult.lastUpdated);
          const now = new Date();
          const ageInMinutes = (now - lastUpdated) / (1000 * 60);

          if (ageInMinutes < 30) {
            console.log(`[SAECS] ✅ Using cached data from database (${Math.round(ageInMinutes)} minutes old)`);
            // Handle both data formats: direct data or wrapped in .data property
            const dataToSet = dbResult.data.data || dbResult.data;
            setScheduleData(dataToSet);
            setIsLoading(false);
            return;
          } else {
            console.log(`[SAECS] Cache is ${Math.round(ageInMinutes)} minutes old, refreshing...`);
          }
        } else {
          console.log('[SAECS] No cached data found in database');
        }
      } else {
        console.log('[SAECS] Force refresh requested, skipping database cache');
      }

      // Fetch fresh data from API
      console.log('[SAECS] Fetching fresh data from DCSA API...');
      const data = await fetchServiceSchedule(SAECS_SERVICE_CODE, null, SAECS_VESSEL_IMOS, VESSELS_PER_GRID);

      if (data.current && data.current.vessels) {
        data.current.vessels = data.current.vessels.slice(0, VESSELS_PER_GRID);
        data.current.loadPorts = data.current.loadPorts.map(port => ({
          ...port,
          schedules: port.schedules.slice(0, VESSELS_PER_GRID)
        }));
        data.current.dischargePorts = data.current.dischargePorts.map(port => ({
          ...port,
          schedules: port.schedules.slice(0, VESSELS_PER_GRID)
        }));
      }

      if (data.upcoming && data.upcoming.vessels) {
        data.upcoming.vessels = data.upcoming.vessels.slice(0, VESSELS_PER_GRID);
        data.upcoming.loadPorts = data.upcoming.loadPorts.map(port => ({
          ...port,
          schedules: port.schedules.slice(0, VESSELS_PER_GRID)
        }));
        data.upcoming.dischargePorts = data.upcoming.dischargePorts.map(port => ({
          ...port,
          schedules: port.schedules.slice(0, VESSELS_PER_GRID)
        }));
      }

      // Save to database in the background (non-blocking)
      DatabaseService.saveServiceSchedules('SAECS', data)
        .then(success => {
          if (success) {
            console.log('[SAECS] ✅ Data saved to database');
          } else {
            console.log('[SAECS] ⚠️ Failed to save data to database (non-critical)');
          }
        })
        .catch(error => {
          console.log('[SAECS] ⚠️ Database save error (non-critical):', error.message);
        });

      setScheduleData(data);
      setError(null);
    } catch (error) {
      console.error('[SAECS] ❌ Error loading schedule data:', error);
      setError('Failed to load schedule data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadScheduleData(false); // Check database first, use cache if available
  }, []);

  if (isLoading) return <LoadingIndicator />;
  if (error) return (
    <div className="flex justify-center items-center h-64">
      <div className="text-red-600 dark:text-red-400">{error}</div>
    </div>
  );
  if (!scheduleData) return (
    <div className="flex justify-center items-center h-64">
      <div className="text-gray-600 dark:text-gray-300">No schedule data available</div>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">SAECS Service Schedule</h1>
        <div className="flex space-x-3">
          <RefreshButton onRefresh={() => loadScheduleData(true)} />
          <ExcelExport data={scheduleData} serviceName="SAECS" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-blue-600 dark:text-blue-400">Primary Schedule</h2>
        </div>
        <ScheduleGrid
          vessels={scheduleData.current.vessels}
          loadPorts={scheduleData.current.loadPorts}
          dischargePorts={scheduleData.current.dischargePorts}
          VesselHeaderComponent={({ vessel, vesselIndex }) => (
            <VesselHeader
              vessel={vessel}
              loadPorts={scheduleData.current.loadPorts}
              vesselIndex={vesselIndex}
            />
          )}
        />
      </div>

      {scheduleData.upcoming.vessels.length > 0 && (
        <div className="space-y-4">
          <div className="mb-2">
            <h2 className="text-lg font-semibold text-green-600 dark:text-green-400">Extended Schedule</h2>
          </div>
          <ScheduleGrid
            vessels={scheduleData.upcoming.vessels}
            loadPorts={scheduleData.upcoming.loadPorts}
            dischargePorts={scheduleData.upcoming.dischargePorts}
            VesselHeaderComponent={({ vessel, vesselIndex }) => (
              <VesselHeader
                vessel={vessel}
                loadPorts={scheduleData.upcoming.loadPorts}
                vesselIndex={vesselIndex}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ServiceScheduleView;